import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AuthService } from '../../../auth/auth.service';
import { UsageReportService } from '../services/usage-report.service';
import { Title } from '@angular/platform-browser';
import * as _ from 'lodash';
import { Subscription } from 'rxjs/Subscription';
import { AppPartUsageTimeSeriesComponent } from '../../../components/app-part-usage-time-series/app-part-usage-time-series.component';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material';
import { UnityChartComponent } from '../../../components/unity-chart/unity-chart.component';
import { UnityChartService } from '../../../components/unity-chart/unity-chart.service';
import { ThresholdModalComponent } from 'app/components/threshold-modal/threshold-modal.component';

@Component({
  templateUrl: './usage-report.component.html',
  styleUrls: ['./usage-report.component.scss'],
  providers: [AuthService, UsageReportService, UnityChartService],
})
export class UsageReportComponent implements OnInit, OnDestroy {
  @ViewChild('unityChart') unityChart: UnityChartComponent;

  public data: any;
  public displayReports: any;
  public displaySummary: any;
  private selectedIndex: Array<number>;
  private selectedItem: Array<any>;
  public loading: boolean;
  public reportLoaded: boolean;
  public isWorkspaceSelected: boolean;
  public filters: { [key: string]: any };
  private subscription: Subscription;

  private eagerLoading = false;
  public show3DComponent: boolean;

  public windowSizeOptions = this.usageReportService.getWindowSizeOptions();
  public reportCriteriaOptions =
    this.usageReportService.getReportCriteriaOptions();
  public dataSourceOptions = this.usageReportService.getDataSourceOptions();
  private labelHierarchy = ['Plant', 'Rack', 'Part'];

  public thresholdValues = [];
  public thresholdLabel = '';

  private tileClassDescriptions = [
    { name: 'red-up', description: 'Very Fast Movement' },
    { name: 'red-down', description: 'Very Slow Movement' },
    { name: 'orange-up', description: 'Fast Movement' },
    { name: 'orange-down', description: 'Slow Movement' },
    { name: 'green-up', description: 'Normal Movement (Slight Increase)' },
    { name: 'green-down', description: 'Normal Movement (Slight Decrease)' },
    { name: 'green-none', description: 'No Change' },
    { name: 'blue-none', description: 'Insufficient History' },
    { name: 'grey-none', description: 'No Usage' },
  ];

  constructor(
    private usageReportService: UsageReportService,
    private authService: AuthService,
    private unityChartService: UnityChartService,
    private dialog: MatDialog,
    private title: Title
  ) {
    this.title.setTitle('OES - Usage Report');
  }

  ngOnInit() {
    this.initValues();
    this.isWorkspaceSelected =
      this.authService.workspaceMasterCustomerID !== null;
    if (this.isWorkspaceSelected) {
      this.runReport();
    }
    this.unityChartService.subscribe({
      next: (v) => {
        const index = this.unityChartService.index;
        const level = this.unityChartService.level;
        if (level === this.selectedItem.length + 1 && level < 4) {
          this.drillDown(index);
        }
      },
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  initValues() {
    this.filters = {
      windowSize: UsageReportService.WINDOW_3M,
      reportCriteria: UsageReportService.REPORT_HITS,
      eagerLoad: this.eagerLoading ? 1 : 0,
      dataSource: UsageReportService.DATA_SOURCE_SD1,
    };
    this.data = {
      records: [],
      reportCriteria: this.filters.reportCriteria,
      reportGenerated: '',
      windowDates: {
        p1Start: '',
        p1End: '',
        p2Start: '',
        p2End: '',
      },
      masterCustomer: {
        CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION: false,
      },
    };
    this.displayReports = {
      label: '',
      view: this.labelHierarchy[0] + ' View',
      records: [],
    };
    this.displaySummary = [];
    this.selectedIndex = [];
    this.selectedItem = [];
    this.loading = false;
    this.reportLoaded = false;
    this.show3DComponent = false;

    this.usageReportService.getUserThresholdValues(null).subscribe((result) => {
      this.thresholdValues = result.data;
      this.updateThresholdLabel(
        this.thresholdValues['lowerValue'],
        this.thresholdValues['upperValue']
      );
    });
  }

  onChangeTab(event) {
    this.show3DComponent = event['index'] === 2;
    if (this.show3DComponent) {
      setTimeout(() => {
        // time out to allow unity-chart component to have been invoked
        this.setDisplayReportData();
      }, 250);
    }
  }

  runReport() {
    this.reportLoaded = false;
    this.getData();
  }

  getData(resetDrillLevel = true, id = 0) {
    if (this.eagerLoading) {
      this.loading = true;
      this.subscription = this.usageReportService
        .getReport(this.filters)
        .subscribe(
          (result) => {
            this.reportLoaded = true;
            const data = result.data;
            data['masterCustomer'][
                'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
                ] =
                Number(
                    data['masterCustomer'][
                        'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
                        ]
                ) === 1;
            data['masterCustomer']['CU0_ALLOW_INVENTORY_MANAGEMENT'] =
                Number(data['masterCustomer']['CU0_ALLOW_INVENTORY_MANAGEMENT']) ===
                1;
            this.dataSourceOptions = this.usageReportService.getDataSourceOptions(
                data['masterCustomer']['CU0_ALLOW_INVENTORY_MANAGEMENT'],
                data['masterCustomer'][
                    'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
                    ]
            );
            this.filters.dataSource = this.usageReportService.getDataSourceChoice(
                this.filters.dataSource,
                this.dataSourceOptions
            );
            this.data = result.data;
            this.selectedIndex = [];
            this.setDisplayReportData();
            this.show3DComponent = false;
            this.loading = false;
          },
          () => {
            this.loading = false;
            this.reportLoaded = false;
          }
        );
    } else {
      this.loading = true;
      const filters = _.clone(this.filters);
      if (resetDrillLevel) {
        this.selectedItem = [];
      } else {
        filters['id'] = id;
      }
      filters['drillLevel'] = this.selectedItem.length;
      this.subscription = this.usageReportService.getReport(filters).subscribe(
        (result) => {
          this.reportLoaded = true;
          const data = result.data;
          data['masterCustomer'][
            'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
          ] =
            Number(
              data['masterCustomer'][
                'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
              ]
            ) === 1;
          this.dataSourceOptions = this.usageReportService.getDataSourceOptions(
              data['masterCustomer']['CU0_ALLOW_INVENTORY_MANAGEMENT'],
              data['masterCustomer'][
                  'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
                  ]
          );
          this.filters.dataSource = this.usageReportService.getDataSourceChoice(
              this.filters.dataSource,
              this.dataSourceOptions
          );
          this.data = data;
          this.setDisplayReportData();
          this.loading = false;
        },
        () => {
          this.loading = false;
          this.reportLoaded = false;
        }
      );
    }
  }

  drillDown(id) {
    if (this.eagerLoading) {
      if (this.displayReports.records[id].records.length > 0) {
        this.selectedIndex.push(id);
        this.setDisplayReportData(true);
      }
    } else {
      if (Number(this.displayReports.records[id].recordCount) > 0) {
        this.loading = true;
        this.selectedItem.push({
          id: this.displayReports.records[id].id,
          label: this.displayReports.records[id].label,
        });
        this.getData(false, this.displayReports.records[id].id);
      } else {
        if (this.selectedItem.length === 2) {
          const dialogRef = this.dialog.open(AppPartUsageTimeSeriesComponent, {
            width: window.innerWidth - 100 + 'px',
            height: window.innerHeight - 80 + 'px',
            role: 'dialog',
            data: {
              MD1_ID: this.displayReports.records[id].id,
              windowSize: this.filters.windowSize,
              reportCriteria: this.filters.reportCriteria,
              dataSource: this.filters.dataSource,
            },
          });
        }
      }
    }
  }

  drillUp() {
    if (this.eagerLoading) {
      if (this.selectedIndex.length > 0) {
        this.selectedIndex.pop();
        this.setDisplayReportData(true);
      }
    } else {
      if (this.selectedItem.length > 0) {
        this.selectedItem.pop();
        if (this.selectedItem.length > 0) {
          this.getData(
            false,
            this.selectedItem[this.selectedItem.length - 1].id
          );
        } else {
          this.getData(true);
        }
      }
    }
  }

  setDisplayReportData(showDrillDownLoader = false) {
    if (this.eagerLoading) {
      this.loading = showDrillDownLoader;
      const drillLevel = this.selectedIndex.length;
      let data = this.data.records.slice();
      let label = '';
      if (drillLevel > 0) {
        for (let tracker = 0; tracker < drillLevel; tracker++) {
          label +=
            (tracker === 0 ? '' : ' > ') +
            this.labelHierarchy[tracker] +
            ': ' +
            data[this.selectedIndex[tracker]].label;
          data = data[this.selectedIndex[tracker]].records;
        }
      }
      this.displayReports = {
        label: label,
        view: this.labelHierarchy[drillLevel] + ' View',
        records: data,
      };

      const classList = [];
      const classListCount = [];
      for (let index = 0; index < data.length; index++) {
        const itemClass = data[index].class;
        let classIndex = classList.indexOf(itemClass);
        if (classIndex < 0) {
          classList.push(itemClass);
          classIndex = classList.length - 1;
          classListCount[classIndex] = 0;
        }
        classListCount[classIndex]++;
      }
      this.displaySummary = [];
      for (let index = 0; index < classList.length; index++) {
        const tileClass = this.tileClassDescriptions.find(
          (tileClassItem) => tileClassItem.name === classList[index]
        );
        this.displaySummary.push({
          colorClass: classList[index] + '-sm',
          recordType:
            this.labelHierarchy[drillLevel] +
            (classListCount[index] > 1 ? 's' : ''),
          label:
            tileClass === undefined
              ? 'Unknown Movement'
              : tileClass.description,
          count: classListCount[index],
        });
      }
      this.loading = false;
    } else {
      const data = this.data.records.slice();
      const drillLevel = this.selectedItem.length;
      let label = '';
      if (drillLevel > 0) {
        for (let tracker = 0; tracker < drillLevel; tracker++) {
          label +=
            (tracker === 0 ? '' : ' > ') +
            this.labelHierarchy[tracker] +
            ': ' +
            this.selectedItem[tracker].label;
        }
      }
      this.displayReports = {
        label: label,
        view: this.labelHierarchy[drillLevel] + ' View',
        records: data,
      };

      const webGLRecords = [];
      const reportCriteria = this.reportCriteriaOptions.find(
        (option) => option.value === Number(this.filters['reportCriteria'])
      );
      const classList = [];
      const classListCount = [];
      for (let index = 0; index < data.length; index++) {
        const itemClass = data[index].class;
        let classIndex = classList.indexOf(itemClass);
        if (classIndex < 0) {
          classList.push(itemClass);
          classIndex = classList.length - 1;
          classListCount[classIndex] = 0;
        }
        classListCount[classIndex]++;

        webGLRecords.push({
          id: index,
          label: data[index]['label'],
          unitOfMeasure:
            reportCriteria !== undefined ? reportCriteria['label'] : '',
          unitDisplay: 'pre',
          p1: data[index]['p1'],
          p2: data[index]['p2'],
        });
      }
      const webGLData = {
        title: this.labelHierarchy[drillLevel] + ' View',
        subtitle: label,
        drillLevel: drillLevel,
        unitOfMeasure: '',
        values: webGLRecords,
      };

      this.displaySummary = [];
      for (let index = 0; index < classList.length; index++) {
        const tileClass = this.tileClassDescriptions.find(
          (tileClassItem) => tileClassItem.name === classList[index]
        );
        this.displaySummary.push({
          colorClass: classList[index] + '-sm',
          recordType:
            this.labelHierarchy[drillLevel] +
            (classListCount[index] > 1 ? 's' : ''),
          label:
            tileClass === undefined
              ? 'Unknown Movement'
              : tileClass.description,
          count: classListCount[index],
        });
      }

      // drill level cannot be > 2
      this.unityChart.displayData(webGLData);
    }
  }

  onSelectDataSource(event: MatRadioChange) {
    this.filters['dataSource'] = event.value;
    if (this.selectedItem.length > 0) {
      this.getData(false, this.selectedItem[this.selectedItem.length - 1].id);
    } else {
      this.getData(true);
    }
  }

  openThresholdModal() {
    const dialogRef = this.dialog.open(ThresholdModalComponent, {
      width: '400px',
      height: '285px',
      role: 'dialog',
      data: {
        lowerValue: this.thresholdValues['lowerValue'],
        upperValue: this.thresholdValues['upperValue'],
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.updateThresholdLabel(result.lowerValue, result.upperValue);
        this.thresholdValues['lowerValue'] = result.lowerValue;
        this.thresholdValues['upperValue'] = result.upperValue;
      }
    });
  }

  updateThresholdLabel(lower: number, upper: number) {
    this.thresholdLabel = lower + '% - ' + upper + '%';
  }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/oes/usage-report/usage-report.component.ts